import L from "leaflet"
import "leaflet/dist/leaflet.css"

type Config = {
  image: HTMLImageElement
  maxHeight?: number
}

export class WebcamPanorama {
  private map: L.Map | null = null
  private container: HTMLDivElement | null = null
  private readonly maxHeight: number

  constructor(private readonly root: HTMLElement, private readonly config: Config) {
    this.maxHeight = config.maxHeight || 480
    this.init()
  }

  destroy() {
    this.map?.off()
    this.map?.stop()
    this.map?.remove()
    this.map = null
    this.container = null
    this.root.replaceChildren()
  }

  private init() {
    const { image } = this.config

    this.container = document.createElement("div")
    this.container.classList.add("webcam-popup__modal-panorama-container")
    this.container.style.height = `${Math.min(this.maxHeight, image.height)}px`
    this.container.style.width = "100%"
    this.root.replaceChildren(this.container)

    const map = L.map(this.container, {
      minZoom: 1,
      maxZoom: 4,
      center: [0, 0],
      zoom: 1,
      crs: L.CRS.Simple,
      maxBoundsViscosity: 1.0,
    })

    this.map = map

    const height = Math.round(this.container.offsetHeight)
    const width = Math.round((height * image.width) / image.height)
    const bounds = new L.LatLngBounds(
      map.layerPointToLatLng(L.point(0, 0)),
      map.layerPointToLatLng(L.point(width, height)),
    )

    L.imageOverlay(image.src, bounds).addTo(map)
    map.setMaxBounds(bounds)

    map.on("drag", () => {
      map.panInsideBounds(bounds, { animate: false })
    })

    const duration = (20 * width) / height
    let dir = "left"

    const scrollAnimation = () => {
      if (!this.container) return

      dir = dir === "right" ? "left" : "right"

      const x =
        dir === "right"
          ? Math.round(width - this.container.offsetWidth / 2) - 1
          : Math.round(this.container.offsetWidth / 2) + 1

      const center = map.layerPointToLatLng(L.point(x, Math.round(height / 2)))
      const options = {
        animate: true,
        pan: {
          duration,
          easeLinearity: 1,
        },
      } as unknown as L.ZoomPanOptions

      map.setView(center, map.getZoom(), options)
    }

    map.addEventListener("moveend", scrollAnimation)
    scrollAnimation()

    const stopScrollAnimation = () => {
      map.removeEventListener("moveend", scrollAnimation)
      map.removeEventListener("click", stopScrollAnimation)
      map.removeEventListener("dragstart", stopScrollAnimation)
      map.removeEventListener("zoom", stopScrollAnimation)
      map.stop()
    }

    map.on("click", stopScrollAnimation)
    map.on("dragstart", stopScrollAnimation)
    map.on("zoom", stopScrollAnimation)
  }
}
